import * as React from "react";
import { connect } from "react-redux";
import { FormattedNumber } from "react-intl";
import SwipeableViews from "react-swipeable-views";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormHelperText,
  Grid,
  MobileStepper,
  Slider,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core";

import PercentageNumber from "../../components/UI/PercentageNumber";
import CurrencyNumber from "../../components/UI/CurrencyNumber";
import DoubleArrowRight from "../../components/Icons/DoubleArrowRight";
import DateField, {
  isValid as dateFieldValid,
} from "../../components/DateField";
import { CyNumber } from "../../utilities/number";
import ThinArrowLeft from "../../components/Icons/ThinArrowLeft";
import ThinArrowRight from "../../components/Icons/ThinArrowRight";
import {
  defaultClientState,
  clientFields,
  ssiConfig,
} from "../../utilities/constant";
import { isEmail, isPhoneNumber } from "../../utilities/validators";
import Loading from "../../components/Loading/Loading";
import SectionHeader from "../../components/UI/SectionHeader";
import SectionCaption from "../../components/UI/SectionCaption";
import SeeImportantDisclosures from "../../components/UI/SeeImportantDisclosures";
import PhoneNumberField from "../../components/Forms/PhoneNumberField";
import MinimumRequiredDialog from "../../components/UI/MinimumRequiredDialog";
import usStatesFull from "../../utilities/us-states-full";

import {
  appAction,
  clientAction,
  householdAction,
  portfolioAction,
} from "../../actions";
import moment from "moment";

const styles = (theme) => {
  return {
    contentWrapper: {
      height: "100%",
      width: `100%`,
      display: `flex`,
      flexDirection: "column",
      position: "relative",
    },
    sliderWrapper: {
      display: "flex",
      alignItems: "center",
      margin: "10px 0",
      width: "100%",
      minHeight: "calc(100% - 200px)",
      [theme.breakpoints.down("md")]: {
        minHeight: 0,
      },
      position: "relative",
      "& > div": {
        width: "100%",
      },
    },
    sliderPanel: {
      minHeight: 500,
      overflow: "hidden",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
    },
    sliderControl: {
      minWidth: 38,
      [theme.breakpoints.down("md")]: {
        marginBottom: "25%",
      },
    },
    sliderControlDisabled: {
      opacity: 0.24,
    },
    slide: {
      padding: "0 20px",
      height: "100%",
      [theme.breakpoints.down("md")]: {
        height: "auto",
      },
      display: "flex",
      flexFlow: "column",
      justifyContent: "center",
    },
    form: {
      maxWidth: "100%",
      padding: "10px 0",
      "&.disabled": {
        display: "none",
      },
    },
    row: {
      display: "block",
      flexWrap: "wrap",
      "& h1": {
        [theme.breakpoints.down("md")]: {
          fontSize: 18,
        },
      },
    },
    heading: {
      [theme.breakpoints.down("md")]: {
        fontSize: 18,
      },
    },
    input: {
      margin: theme.spacing(1, 0),
      color: theme.palette.grey["A400"],
      minWidth: 300,
      [theme.breakpoints.down("md")]: {
        margin: "2px",
      },
      "& label": {
        fontSize: 16,
      },
      "& label > span": {
        color: theme.palette.error.main,
      },
      "& input": {
        [theme.breakpoints.down("md")]: {
          fontSize: 16,
        },
      },
    },

    checkLabel: {
      display: "inline-block",
    },
    inputEmail: {
      minWidth: "420px",
    },
    line: {
      fontSize: 24,
      [theme.breakpoints.down("md")]: {
        fontSize: 16,
        lineHeight: 1,
      },
      margin: 0,
      "& span": {
        color: theme.palette.primary.main,
      },
    },
    sliderLegend: {
      width: "60%",
      height: "10px",
      position: "absolute",
      top: 0,
      display: "flex",
      justifyContent: "space-between",
    },
    sliderLegendNum: {
      width: 80,
      textAlign: "center",
      fontSize: 16,
      "&:not(:first-child):not(:last-child)": {
        [theme.breakpoints.down("md")]: {
          display: "none",
        },
      },
    },
    slider: {
      maxWidth: "calc(60% - 54px)",
      marginLeft: "27px",
    },
    expansionPanel: {
      background: "transparent",
      margin: 0,
    },
    expansionPanelExpanded: {
      margin: 0,
    },
    expansionPanelSummary: {
      padding: 0,
      minHeight: 1,
    },
    expansionPanelSummaryExpanded: {
      padding: 0,
      minHeight: 1,
    },
    expansionPanelDetails: {
      padding: "20px 0 10px",
      position: "relative",
    },
    controller: {
      width: theme.wbiComponent.ctaController.totalWidth,
      [theme.breakpoints.down("md")]: {
        width: "45px",
      },
      height: "100%",
      position: "fixed",
      right: 0,
      top: 0,
      zIndex: 0,
      background: `linear-gradient(-189deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 94%)`,
      boxShadow: "inset 0 0 17px rgba(0,0,0,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      "& button": {
        height: 360,
        width: "100%",
        background: "transparent",
        // background: 'linear-gradient(-12deg, #FAA42F 45%, #FCCD8C 68%)',
        // boxShadow: '0 0 6px 0 rgba(0,0,0,0.20)',
        cursor: "pointer",
        border: "none",
      },
      "@media print": {
        display: "none",
      },
    },
    controllerDisabled: {
      background:
        "linear-gradient(-189deg, rgb(214, 214, 214) 0%, rgb(125, 125, 125) 94%)",
    },
    overridedInput: {
      marginTop: 0,
      marginBottom: 0,
      "& input": {
        color: theme.palette.primary.main,
        [theme.breakpoints.down("md")]: {
          fontSize: 16,
        },
      },
    },
    saveButtonNavItem: {
      width: theme.wbiComponent.staticNavigation.totalWidth,
      left: 0,
      textTransform: "uppercase",
      color: theme.palette.grey[50],
      fontSize: 14,
      [theme.breakpoints.down("md")]: {
        fontSize: 12,
        marginLeft: "-12px",
      },
      "& svg": {
        [theme.breakpoints.down("md")]: {
          width: 18,
          marginLeft: 7,
        },
      },
      "&:disabled": {
        cursor: "not-allowed",
      },
    },
    profileBtn: {
      [theme.breakpoints.down("md")]: {
        fontSize: 12,
      },
    },
  };
};

class AddClient extends React.Component {
  state = {
    client: { ...defaultClientState },
    spouse: { ...defaultClientState },
    household: "Household (Combined)",
    householdName: "",
    overriding: [],
    activeStep: 0,
    isFormValid: false,
    disableSpouseCheckbox: false,
    includeSpouse: false,
    minInitInvestDialogOpen: false,
    overMinInvest: false,
  };

  handleStepChange = (activeStep) => {
    if (
      this.state.client.firstName &&
      this.state.client.lastName &&
      this.state.householdName
    ) {
      this.setState({ activeStep });
    }
  };

  handleCheck = (e, includeSpouse) => {
    this.setState({ includeSpouse }, this.checkFormValid);
  };

  handleNext = () => {
    this.setState((prevState) => ({
      activeStep: prevState.activeStep + 1,
    }));
  };

  handleBack = () => {
    this.setState((prevState) => ({
      activeStep: prevState.activeStep - 1,
    }));
  };

  handleInput = (payload) => (e) => {
    let newState = { ...this.state[payload.key] };
    newState[payload.field] = e.target.value;
    this.setState({ [payload.key]: newState }, this.checkFormValid);
  };

  handleSlider = (key, fieldName) => (event, newValue) => {
    let newState = { ...this.state[key] };
    newState[fieldName] = newValue;
    this.setState({ [key]: newState }, this.checkFormValid);
  };

  handleOverride = (payload) => (e) => {
    let newState = { ...this.state[payload.key] };

    const value = e.target.value.replace(/[^0-9.]+/gi, "");

    // If this is related to percentage field then need to divide the value by 100 before setting to newState
    // Also need to handle the scenario of using backspace
    if (e.target.defaultValue.indexOf("%") !== -1) {
      let tempVal = e.target.value;
      if (tempVal.indexOf("%") === -1 && tempVal.length > 0) {
        if (value.length === 1) {
          tempVal = 0;
        } else {
          tempVal = value.substring(0, value.length - 1);
        }
      } else {
        tempVal = value;
      }

      if (tempVal !== 0 && tempVal !== undefined) {
        newState[payload.field] = Number(tempVal) / 100;
      } else {
        newState[payload.field] = 0;
      }
    } else {
      newState[payload.field] = Number(value);
    }

    this.setState({ [payload.key]: newState }, this.checkFormValid);
  };

  enableOverride = (e) => {
    let arr = this.state.overriding;
    arr.push(this.state.expanded);
    this.setState({ overriding: arr });
  };

  resetOverride = (e, f, type) => {
    const { client, spouse } = this.state;
    const datum = type === "client" ? client : spouse;
    datum[f.fieldName] = defaultClientState[f.fieldName];
    this.handleOverride({ key: type, field: f.fieldName });
    let arr = this.state.overriding;
    let index = arr.indexOf(this.state.expanded);
    if (index > -1) {
      arr.splice(index, 1);
    }
    this.setState({ overriding: arr });
  };

  toggleExpansion = (name) => (event, expanded) => {
    this.setState({ expanded: expanded ? name : false });
  };

  saveClient = () => {
    const { householdId, currentHousehold } = this.props;
    if (!this.state.overMinInvest) {
      this.setState({
        minInitInvestDialogOpen: true,
        minInitInvestAmount: "$50,000",
        minInitInvestDialogInstruction:
          "Please edit the investment amount to proceed.",
      });
    } else {
      if (this.state.isFormValid) {
        let combinedExpBR = Math.max(
          this.state["client"].monthlyExpensesBR * 12,
          (this.state["client"].currentIncome +
            this.state["spouse"].currentIncome) *
            this.state["client"].expensesPercentageToIncomeBR
        );
        let combinedExpIR = Math.max(
          this.state["client"].monthlyExpensesIR * 12,
          (this.state["client"].currentIncome +
            this.state["spouse"].currentIncome) *
            this.state["client"].expensesPercentageToIncomeIR
        );
        let data = [];
        data.push({
          profile: {
            ...this.state.client,
            currentAge: moment().diff(
              moment(this.state.client.dob, "MM/DD/YYYY"),
              "years",
              false
            ),
            primary: true,
            householdName: this.state.householdName,
            combinedAnnualExpenseBR: combinedExpBR,
            combinedAnnualExpenseIR: combinedExpIR,
          },
        });
        if (this.state.includeSpouse) {
          data.push({
            profile: {
              ...this.state.spouse,
              currentAge: moment().diff(
                moment(this.state.spouse.dob, "MM/DD/YYYY"),
                "years",
                false
              ),
              primary: false,
            },
          });
        }
        let householdSettings = this.props.user.advisorConfig;
        if (this.props.user.user.advisorConfig)
          householdSettings = this.props.user.user.advisorConfig;
        if (currentHousehold && currentHousehold.advisorConfig)
          householdSettings = currentHousehold.advisorConfig;

        let payload = {
          clientData: data,
          advisorConfig: { ...householdSettings, ...ssiConfig },
        };
        if (this.props.clientInit) {
          this.props.dispatch(clientAction.createClient(payload));
        } else {
          this.props.dispatch(clientAction.updateClient(householdId, payload));
        }
      }
    }
  };

  /*
    Check if the primary / spouse form is valid*,
    the minimum investment amount is over 50k,
    and update the state of isFormValid / overMinInvest

    *Valid: Non empty string on the required fields (first name / last name of client(s))
  */
  checkFormValid = () => {
    const { client, spouse, includeSpouse, overMinInvest } = this.state;

    const hasCompletedNameStep = this.hasCompletedNameStep();
    const hasCompletedContactInfoStep = this.hasCompletedContactInfoStep();
    const isFormValid = hasCompletedNameStep && hasCompletedContactInfoStep;

    let checkMinInvest = overMinInvest;
    if (includeSpouse) {
      if (
        client.currentQualifiedPlans +
          spouse.currentQualifiedPlans +
          client.currentNonQualifiedSavings <
        10000
      ) {
        checkMinInvest = false;
      } else {
        checkMinInvest = true;
      }
    } else if (
      client.currentQualifiedPlans + client.currentNonQualifiedSavings <
      10000
    ) {
      checkMinInvest = false;
    } else {
      checkMinInvest = true;
    }
    this.setState({ isFormValid, overMinInvest: checkMinInvest });
  };

  componentDidMount() {
    const { householdId, currentHousehold } = this.props;
    this.props.dispatch(portfolioAction.clearCurrentPortfolio());
    this.props.dispatch(appAction.toggleMainNav(false, false));
    this.props.dispatch(appAction.setAddClientNav(true));
    this.props.dispatch(appAction.updateProgressStep(1));
    window.scrollTo(0, 0);

    if (currentHousehold) {
      this.setState({ householdName: currentHousehold.name || "" });
      currentHousehold.Clients.forEach((c) => {
        if (c.profile.primary) {
          this.setState(
            {
              client: Object.assign(
                { id: c.id },
                defaultClientState,
                c.profile
              ),
            },
            this.checkFormValid
          );
        } else {
          this.setState(
            {
              spouse: Object.assign(
                { id: c.id },
                defaultClientState,
                c.profile
              ),
              disableSpouseCheckbox: true,
              includeSpouse: true,
            },
            this.checkFormValid
          );
        }
      });
    }

    if (!this.props.clientInit) {
      this.props.dispatch(householdAction.getHouseholdById(householdId));
    }
  }

  hasCompletedNameStep() {
    const { client, householdName, includeSpouse, spouse } = this.state;
    const clientHasValidName = !!client.firstName.trim() && !!client.lastName.trim();
    const spouseHasValidName = includeSpouse ? (!!spouse.firstName.trim() && !!spouse.lastName.trim()) : true;
    return (
      !!householdName.trim() &&
      clientHasValidName &&
      spouseHasValidName
    );
  }

  hasCompletedContactInfoStep() {
    const { includeSpouse, spouse } = this.state;
    const { streetAddress, phone, homePhone, city, state, zipCode, email, dob } =
      this.state.client;

    const isPhoneValid = (number) => {
      if (!number) return true;
      return isPhoneNumber(number);
    }

    const isEmailValid = (email) => {
      if (!email) return true;
      return isEmail(email);
    }

    const clientHasValidContact =
      !!streetAddress.trim() &&
      !!city.trim() &&
      !!state.trim() &&
      !!zipCode.trim() &&
      isPhoneValid(phone) &&
      isPhoneValid(homePhone) &&
      dateFieldValid(dob) &&
      isEmailValid(email);
    const spouseHasValidContact = includeSpouse ?
      isPhoneValid(spouse.phone) &&
      isPhoneValid(spouse.homePhone) &&
      dateFieldValid(spouse.dob) &&
      isEmailValid(spouse.email) :
      true;
    return (
      clientHasValidContact && spouseHasValidContact
    );
  }

  goToNextStep(householdId) {
    this.props.history.push(`/Portal/household/${householdId}/risk`);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { currentHousehold } = this.props;

    // Finishing update a client
    if (
      !this.props.clientInit &&
      this.props.client.clientUpdating === true &&
      nextProps.client.clientUpdating === false
    ) {
      this.goToNextStep(nextProps.currentHousehold.id);
    }

    if (!currentHousehold && nextProps.currentHousehold) {
      if (!this.props.clientInit) {
        // Handles event when fetching an existing portfolio and clients
        nextProps.currentHousehold.Clients.forEach((c) => {
          if (c.profile.primary) {
            this.setState(
              {
                client: Object.assign(
                  { id: c.id },
                  defaultClientState,
                  c.profile
                ),
                householdName: nextProps.currentHousehold.name || "",
              },
              this.checkFormValid
            );
          } else {
            this.setState(
              {
                spouse: Object.assign(
                  { id: c.id },
                  defaultClientState,
                  c.profile
                ),
                disableSpouseCheckbox: true,
                includeSpouse: true,
              },
              this.checkFormValid
            );
          }
        });
      } else {
        // Redirect to Risk page when creating new clients completed
        this.goToNextStep(nextProps.currentHousehold.id);
      }
    }
  }

  render() {
    const { classes, portfolio, clientInit, currentHousehold } = this.props;
    const {
      client,
      spouse,
      household,
      isFormValid,
      disableSpouseCheckbox,
      includeSpouse,
      minInitInvestDialogOpen,
      minInitInvestAmount,
      minInitInvestDialogInstruction,
    } = this.state;
    if (!clientInit && portfolio.requesting) {
      return <Loading />;
    }

    const householdName = this.state.householdName || "";

    const clientEmailHasError =
      this.state.client.email && !isEmail(this.state.client.email);
    const spouseEmailHasError =
      this.state.spouse.email && !isEmail(this.state.spouse.email);
    const clientDOBHasError =
      this.state.client.dob && !dateFieldValid(this.state.client.dob);
    const spouseDOBHasError =
      this.state.spouse.dob && !dateFieldValid(this.state.spouse.dob);
    const clientFirstUpdateDOB =
      !clientInit && !dateFieldValid(this.state.client.dob);
    const spouseFirstUpdateDOB =
      !clientInit && !dateFieldValid(this.state.spouse.dob);
    const isFirstStep = this.state.activeStep === 0;
    const isLastStep = this.state.activeStep === 5;
    const hasCompletedNameStep = this.hasCompletedNameStep();
    const hasCompletedContactInfoStep = this.hasCompletedContactInfoStep();
    const isNextStepDisabled =
      isLastStep ||
      !hasCompletedNameStep ||
      (this.state.activeStep === 1 && !hasCompletedContactInfoStep);

    return (
      <React.Fragment>
        <div className={classes.contentWrapper}>
          <SectionHeader text={"Profile"} />
          <SectionCaption>First, let's gather some information.</SectionCaption>
          <div className={classes.sliderWrapper}>
            <Button
              disableRipple
              disableFocusRipple
              classes={{
                root: classes.sliderControl,
                disabled: classes.sliderControlDisabled,
              }}
              size="small"
              onClick={this.handleBack}
              disabled={isFirstStep}
            >
              <ThinArrowLeft />
            </Button>
            <SwipeableViews
              axis={"x"}
              index={this.state.activeStep}
              onChangeIndex={this.handleStepChange}
              disabled
            >
              <div className={classes.slide}>
                <div className={classes.form}>
                  <Grid container>
                    <Grid item>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        required={true}
                        error={currentHousehold && !householdName}
                        helperText={
                          currentHousehold && !householdName
                            ? "Please enter household name"
                            : ""
                        }
                        label={`Household Name`}
                        className={`${classes.input}`}
                        inputProps={{ "aria-label": `Household Name` }}
                        onChange={(e) =>
                          this.setState(
                            { householdName: e.target.value },
                            this.checkFormValid
                          )
                        }
                        value={householdName}
                      />
                    </Grid>
                  </Grid>
                </div>
                <div className={classes.form}>
                  {client && this.renderClientContact("client")}
                </div>
                <div className={classes.row}>
                  <label>
                    <Typography
                      className={`${classes.input} ${classes.checkLabel}`}
                    >
                      <Checkbox
                        onChange={this.handleCheck}
                        checked={includeSpouse}
                        color="primary"
                        disabled={disableSpouseCheckbox}
                      />
                      Include Spouse/Partner?
                    </Typography>
                  </label>
                </div>
                <div className={classes.form}>
                  {includeSpouse && this.renderClientContact("spouse")}
                </div>
              </div>
              {hasCompletedNameStep && (
                <div className={classes.slide}>
                  <Typography variant="body1">
                    <strong>All information must pertain to the client. Valid mobile phone and email address is required for DocuSign.</strong>
                  </Typography>
                  <div className={classes.form}>
                    <Typography
                      variant="h5"
                      color={"primary"}
                      className={classes.heading}
                    >{`${this.state.client.firstName} ${this.state.client.lastName}`}</Typography>
                    <Grid container spacing={1}>
                      <Grid item>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          label="Email Address"
                          type="email"
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client Email Address` }}
                          onChange={this.handleInput({
                            key: "client",
                            field: "email",
                          })}
                          value={this.state["client"].email}
                          error={clientEmailHasError}
                        />
                        {clientEmailHasError && (
                          <FormHelperText error>
                            Please enter a valid email address
                          </FormHelperText>
                        )}
                      </Grid>
                      <Grid item>
                        <DateField
                          InputLabelProps={{ shrink: true }}
                          required
                          label="Date of Birth"
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client Date of Birth` }}
                          onChange={this.handleInput({
                            key: "client",
                            field: "dob",
                          })}
                          value={this.state["client"].dob}
                          error={clientDOBHasError}
                        />
                        {clientFirstUpdateDOB && (
                          <FormHelperText error>
                            This will affect current age in calculations
                          </FormHelperText>
                        )}
                        {clientDOBHasError && (
                          <FormHelperText error>
                            Please enter a valid date
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item>
                        <PhoneNumberField
                          InputLabelProps={{ shrink: true }}
                          label="Mobile Phone"
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client Mobile Phone` }}
                          onChange={this.handleInput({
                            key: "client",
                            field: "phone",
                          })}
                          value={this.state.client.phone}
                        />
                        {this.state.client.phone && !isPhoneNumber(this.state.client.phone) && (
                          <FormHelperText error>
                            Please enter a valid phone number
                          </FormHelperText>
                        )}
                      </Grid>
                      <Grid item>
                        <PhoneNumberField
                          InputLabelProps={{ shrink: true }}
                          label="Home Phone"
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client Home Phone` }}
                          onChange={this.handleInput({
                            key: "client",
                            field: "homePhone",
                          })}
                          value={this.state.client.homePhone}
                        />
                        {this.state.client.homePhone && !isPhoneNumber(this.state.client.homePhone) && (
                          <FormHelperText error>
                            Please enter a valid phone number
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          required
                          label="Street Address"
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client Street Address` }}
                          onChange={this.handleInput({
                            key: "client",
                            field: "streetAddress",
                          })}
                          value={this.state["client"].streetAddress}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          required
                          label="City"
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client City` }}
                          onChange={this.handleInput({
                            key: "client",
                            field: "city",
                          })}
                          value={this.state["client"].city}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          label="State"
                          name="state"
                          select
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client State` }}
                          value={this.state["client"].state || ""}
                          onChange={this.handleInput({
                            key: "client",
                            field: "state",
                          })}
                          margin="normal"
                          required
                          fullWidth
                          SelectProps={{
                            native: true,
                          }}
                        >
                          <option value={""} />
                          {Object.keys(usStatesFull).map((stateAbbr) => (
                            <option key={stateAbbr} value={stateAbbr}>
                              {usStatesFull[stateAbbr]}
                            </option>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item>
                        <TextField
                          InputLabelProps={{ shrink: true }}
                          required
                          label="Zip Code"
                          className={`${classes.input}`}
                          inputProps={{ "aria-label": `Client Zip Code` }}
                          onChange={this.handleInput({
                            key: "client",
                            field: "zipCode",
                          })}
                          value={this.state["client"].zipCode}
                        />
                      </Grid>
                    </Grid>
                  </div>
                  {includeSpouse && (
                    <div className={classes.form}>
                      <Typography
                        variant="h5"
                        color={"primary"}
                        className={classes.heading}
                      >{`${this.state.spouse.firstName} ${this.state.spouse.lastName}`}</Typography>
                      <Grid container spacing={1}>
                        <Grid item>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            label="Email Address"
                            type="email"
                            className={`${classes.input}`}
                            inputProps={{
                              "aria-label": `Spouse Email Address`,
                            }}
                            onChange={this.handleInput({
                              key: "spouse",
                              field: "email",
                            })}
                            value={this.state["spouse"].email}
                            error={spouseEmailHasError}
                          />
                          {spouseEmailHasError && (
                            <FormHelperText error>
                              Please enter a valid email address
                            </FormHelperText>
                          )}
                        </Grid>
                        <Grid item>
                          <DateField
                            required
                            InputLabelProps={{ shrink: true }}
                            label="Date of Birth"
                            className={`${classes.input}`}
                            inputProps={{
                              "aria-label": `Spouse Date of Birth`,
                            }}
                            onChange={this.handleInput({
                              key: "spouse",
                              field: "dob",
                            })}
                            value={this.state["spouse"].dob}
                            error={spouseDOBHasError}
                          />
                          {spouseFirstUpdateDOB && (
                            <FormHelperText error>
                              This will affect current age in calculations
                            </FormHelperText>
                          )}
                          {spouseDOBHasError && (
                            <FormHelperText error>
                              Please enter a valid date
                            </FormHelperText>
                          )}
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item>
                          <PhoneNumberField
                            label="Mobile Phone"
                            InputLabelProps={{ shrink: true }}
                            className={`${classes.input}`}
                            inputProps={{ "aria-label": `Spouse Mobile Phone` }}
                            onChange={this.handleInput({
                              key: "spouse",
                              field: "phone",
                            })}
                            value={this.state.spouse.phone}
                          />
                          {this.state.spouse.phone && !isPhoneNumber(this.state.spouse.phone) && (
                          <FormHelperText error>
                            Please enter a valid phone number
                          </FormHelperText>
                        )}
                        </Grid>
                        <Grid item>
                          <PhoneNumberField
                            label="Home Phone"
                            InputLabelProps={{ shrink: true }}
                            className={`${classes.input}`}
                            inputProps={{ "aria-label": `Spouse Home Phone` }}
                            onChange={this.handleInput({
                              key: "spouse",
                              field: "homePhone",
                            })}
                            value={this.state.spouse.homePhone}
                          />
                          {this.state.spouse.homePhone && !isPhoneNumber(this.state.spouse.homePhone) && (
                          <FormHelperText error>
                            Please enter a valid phone number
                          </FormHelperText>
                        )}
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </div>
              )}
              {hasCompletedNameStep &&
                hasCompletedContactInfoStep &&
                Array(4)
                  .fill("")
                  .map((s, index) => {
                    return (
                      <div className={classes.slide} key={`slider-${index}`}>
                        <div className={classes.form} hidden={index === 3}>
                          {client && this.renderClient("client", index)}
                        </div>
                        <div className={classes.form} hidden={index === 3}>
                          {includeSpouse &&
                            spouse &&
                            this.renderClient("spouse", index)}
                        </div>
                        <div className={classes.form}>
                          {household && this.renderClient("household", index)}
                        </div>
                      </div>
                    );
                  })}
            </SwipeableViews>
            <Button
              disableRipple
              disableFocusRipple
              classes={{
                root: classes.sliderControl,
                disabled: classes.sliderControlDisabled,
              }}
              size="small"
              onClick={this.handleNext}
              disabled={isNextStepDisabled}
            >
              <ThinArrowRight />
            </Button>
          </div>
          <MobileStepper
            steps={6}
            position="static"
            activeStep={this.state.activeStep}
            className={classes.mobileStepper}
            backButton={""}
            nextButton={""}
          />
          <SeeImportantDisclosures />
          <div
            className={`${classes.controller} ${
              !isFormValid ? classes.controllerDisabled : ""
            }`}
          >
            <button
              className={classes.saveButtonNavItem}
              disabled={!isFormValid || this.props.client.clientUpdating}
              onClick={this.saveClient}
            >
              Save
              <DoubleArrowRight />
            </button>
          </div>
        </div>
        <MinimumRequiredDialog
          open={minInitInvestDialogOpen}
          onClose={() => this.setState({ minInitInvestDialogOpen: false })}
          minAmount={minInitInvestAmount}
          message={minInitInvestDialogInstruction}
        />
      </React.Fragment>
    );
  }

  renderClientContact(type) {
    const { classes } = this.props;
    const datum = type === "client" ? "Client" : "Spouse/Partner";
    let nameRequired = false;

    if (type === "spouse") {
      nameRequired = this.state.includeSpouse;
    } else {
      nameRequired = true;
    }

    return (
      <React.Fragment>
        <Grid container spacing={1}>
          <Grid item>
            <TextField
              InputLabelProps={{ shrink: true }}
              required={nameRequired}
              label={`${datum} First Name`}
              className={`${classes.input}`}
              inputProps={{ "aria-label": `${datum} First Name` }}
              onChange={this.handleInput({ key: type, field: "firstName" })}
              value={this.state[type].firstName}
            />
          </Grid>
          <Grid item>
            <TextField
              InputLabelProps={{ shrink: true }}
              required={nameRequired}
              label={`${datum} Last Name`}
              className={`${classes.input}`}
              inputProps={{ "aria-label": `${datum} Last Name` }}
              onChange={this.handleInput({ key: type, field: "lastName" })}
              value={this.state[type].lastName}
            />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }

  renderClient(type, sliderIndex) {
    const { classes } = this.props;
    const { client, spouse, household, expanded } = this.state;
    const datum = type === "spouse" ? spouse : client;
    const defaultKey = type === "spouse" ? "spouse" : "client";

    return (
      <React.Fragment>
        {
          <div className={classes.row}>
            <Typography variant="h5" color={"primary"}>
              {sliderIndex !== 3 && this.state[type].firstName
                ? this.state[type].firstName + " "
                : ""}
              {sliderIndex !== 3 && this.state[type].lastName
                ? this.state[type].lastName
                : ""}
              {(sliderIndex === 1 || sliderIndex === 3) && type === "household"
                ? household
                : ""}
            </Typography>
          </div>
        }
        {clientFields.map((f, i) => {
          if (f.slider !== sliderIndex) return null;
          if (
            ((type === "client" || type === "spouse") && f.householdCommon) ||
            (type === "household" && f.householdCommon === false)
          )
            return null;
          return (
            <div className={classes.row} key={i}>
              <Accordion
                classes={{
                  root: classes.expansionPanel,
                  expanded: classes.expansionPanelExpanded,
                }}
                expanded={expanded === `${type}-${i}`}
                onChange={this.toggleExpansion(`${type}-${i}`)}
                elevation={0}
                square={true}
                TransitionProps={{ timeout: 0 }}
              >
                <AccordionSummary
                  classes={{
                    root: classes.expansionPanelSummary,
                    expanded: classes.expansionPanelSummaryExpanded,
                  }}
                >
                  {f.valueFormat === "dollar" && (
                    <FormattedNumber
                      value={datum[f.fieldName]}
                      currency="USD"
                      // eslint-disable-next-line react/style-prop-object
                      style="currency"
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    >
                      {(value) => (
                        <p
                          className={classes.line}
                          dangerouslySetInnerHTML={{
                            __html: f.line.replace("%", value),
                          }}
                        />
                      )}
                    </FormattedNumber>
                  )}
                  {f.valueFormat === "percentage" && (
                    <p
                      className={classes.line}
                      dangerouslySetInnerHTML={{
                        __html: f.line.replace(
                          "%",
                          `${(datum[f.fieldName] * 100).toFixed(2)}%`
                        ),
                      }}
                    />
                  )}
                  {f.valueFormat === "number" && (
                    <p
                      className={classes.line}
                      dangerouslySetInnerHTML={{
                        __html: f.line.replace("%", datum[f.fieldName]),
                      }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails className={classes.expansionPanelDetails}>
                  <Grid
                    container
                    wrap="nowrap"
                    alignItems="center"
                    justify="space-between"
                  >
                    {this.state.overriding &&
                    this.state.overriding.find((el) => {
                      return el === `${type}-${i}`;
                    }) ? (
                      this.renderOverride(f, i, defaultKey)
                    ) : (
                      <React.Fragment>
                        <div className={classes.sliderLegend}>
                          {f.valueFormat === "dollar" &&
                            Array(f.sliderParam.tickNumber)
                              .fill("")
                              .map((el, index) => {
                                return (
                                  <div
                                    className={classes.sliderLegendNum}
                                    key={"el" + index}
                                  >
                                    <CurrencyNumber
                                      value={
                                        ((f.sliderParam.max -
                                          f.sliderParam.min) /
                                          (f.sliderParam.tickNumber - 1)) *
                                          index +
                                        f.sliderParam.min
                                      }
                                    />
                                  </div>
                                );
                              })}
                          {f.valueFormat === "percentage" &&
                            Array(f.sliderParam.tickNumber)
                              .fill("")
                              .map((el, index) => {
                                return (
                                  <div
                                    className={classes.sliderLegendNum}
                                    key={"el" + index}
                                  >
                                    <PercentageNumber
                                      value={
                                        ((f.sliderParam.max -
                                          f.sliderParam.min) /
                                          (f.sliderParam.tickNumber - 1)) *
                                          index +
                                        f.sliderParam.min
                                      }
                                    />
                                  </div>
                                );
                              })}
                          {f.valueFormat === "number" &&
                            Array(f.sliderParam.tickNumber)
                              .fill("")
                              .map((el, index) => {
                                return (
                                  <div
                                    className={classes.sliderLegendNum}
                                    key={"el" + index}
                                  >
                                    {((f.sliderParam.max - f.sliderParam.min) /
                                      (f.sliderParam.tickNumber - 1)) *
                                      index +
                                      f.sliderParam.min}
                                  </div>
                                );
                              })}
                        </div>
                        <Slider
                          className={classes.slider}
                          value={datum[f.fieldName]}
                          // defaultValue={datum[f.fieldName]}
                          name={f.fieldName}
                          min={f.sliderParam.min}
                          max={f.sliderParam.max}
                          step={f.sliderParam.step}
                          onChange={this.handleSlider(defaultKey, f.fieldName)}
                          // onChangeCommitted={this.handleSlider(defaultKey, f.fieldName)}
                        />
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          onClick={this.enableOverride}
                          className={classes.profileBtn}
                        >
                          Customize
                        </Button>
                      </React.Fragment>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              {f.addSpace && <div className={classes.form} />}
            </div>
          );
        })}
      </React.Fragment>
    );
  }

  renderResetField(v, f, i, type) {
    const { classes } = this.props;
    return (
      <TextField
        id={`override-${type}-${i}`}
        margin="none"
        onChange={this.handleOverride({ key: type, field: f.fieldName })}
        value={v}
        className={classes.overridedInput}
      />
    );
  }

  renderOverride(f, i, type) {
    const { client, spouse } = this.state;
    const { classes } = this.props;
    const datum = type === "client" ? client : spouse;
    const { NumberTransformer, CurrencyTransformer, PercentageTransformer } =
      CyNumber;

    const ResetButton = () => (
      <Button
        size="small"
        color="primary"
        variant="contained"
        onClick={(e) => this.resetOverride(e, f, type)}
        className={classes.profileBtn}
      >
        Reset
      </Button>
    );

    if (f.valueFormat === "number") {
      return (
        <React.Fragment>
          <NumberTransformer value={datum[f.fieldName]}>
            {(val) => this.renderResetField(val, f, i, type)}
          </NumberTransformer>
          <ResetButton />
        </React.Fragment>
      );
    } else if (f.valueFormat === "dollar") {
      return (
        <React.Fragment>
          <CurrencyTransformer value={datum[f.fieldName]}>
            {(val) => this.renderResetField(val, f, i, type)}
          </CurrencyTransformer>
          <ResetButton />
        </React.Fragment>
      );
    } else if (f.valueFormat === "percentage") {
      return (
        <React.Fragment>
          <PercentageTransformer value={datum[f.fieldName]}>
            {(val) => this.renderResetField(val, f, i, type)}
          </PercentageTransformer>
          <ResetButton />
        </React.Fragment>
      );
    }
  }
}

AddClient.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  clientInit: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  return {
    householdId: ownProps.match.params.householdId,
    currentHousehold: state.household.currentHousehold,
    user: state.user,
    client: state.client,
    portfolio: state.portfolio,
    appName: state.app.appName,
  };
};
export default withStyles(styles, { withTheme: true })(
  connect(mapStateToProps)(AddClient)
);



// WEBPACK FOOTER //
// ./src/containers/AddClient/index.jsx